import type { SerializedDockview } from "dockview"
import { omit } from "lodash"
import { NODE_TYPES, type SerializedGridNode } from "./dockview-grid-types"

type SerializedGrid = SerializedDockview["grid"]
type SerializedGridWithOptionalRoot = Omit<SerializedGrid, "root"> & { root?: SerializedGrid["root"] | null }

interface GroupPanelViewState {
  id: string
  views: string[]
  activeView?: string
  [key: string]: unknown
}

type SerializedFloatingGroupEntry = NonNullable<SerializedDockview["floatingGroups"]>[number]

function filterGroupViews(group: GroupPanelViewState, allowedPanelIds: Set<string>): GroupPanelViewState | null {
  const { views, activeView } = group

  const { nextViews, foundActive } = views.reduce<{ nextViews: string[], foundActive: boolean }>(
    (acc, id) => {
      if (allowedPanelIds.has(id)) {
        acc.nextViews.push(id)
        if (id === activeView) acc.foundActive = true
      }
      return acc
    },
    { nextViews: [], foundActive: false },
  )

  if (nextViews.length === 0) return null

  return {
    ...group,
    views: nextViews,
    activeView: foundActive ? activeView : nextViews[0],
  }
}

function pruneGrid(
  node: SerializedGridNode,
  allowedPanelIds: Set<string>,
): SerializedGridNode | null {
  if (node.type === NODE_TYPES.LEAF) {
    const data = node.data as GroupPanelViewState
    const nextGroup = filterGroupViews(data, allowedPanelIds)
    if (!nextGroup) {
      return null
    }
    return { ...node, data: nextGroup }
  }

  const children = (node.data as SerializedGridNode[])
    .map((child) => pruneGrid(child, allowedPanelIds))
    .filter(Boolean) as SerializedGridNode[]

  if (children.length === 0) {
    return null
  }

  return { ...node, data: children }
}

function collectGroupIds(node: SerializedGridNode, acc: string[] = []): string[] {
  if (node.type === NODE_TYPES.LEAF) {
    const data = node.data as GroupPanelViewState
    acc.push(data.id)
    return acc
  }

  const children = node.data as SerializedGridNode[]
  children.forEach(child => collectGroupIds(child, acc))
  return acc
}

function createEmptyGridRoot(grid: SerializedGridWithOptionalRoot): SerializedGridNode {
  const placeholderGroupId = grid.root
    ? collectGroupIds(grid.root)[0] ?? "empty-grid"
    : "empty-grid"

  return {
    type: NODE_TYPES.LEAF,
    data: {
      id: placeholderGroupId,
      views: [],
    },
  }
}

export function ensureGridRoot(grid: SerializedGridWithOptionalRoot): SerializedGrid {
  if (grid.root) {
    return grid as SerializedGrid
  }

  return {
    ...grid,
    root: createEmptyGridRoot(grid),
  }
}

function pruneFloatingGroups(
  floatingGroups: SerializedDockview["floatingGroups"],
  allowedPanelIds: Set<string>,
): SerializedDockview["floatingGroups"] {
  if (!floatingGroups?.length) {
    return undefined
  }

  const prunedGroups = floatingGroups
    .map((floatingGroup) => {
      const nextGroup = filterGroupViews(floatingGroup.data as GroupPanelViewState, allowedPanelIds)
      if (!nextGroup) {
        return null
      }
      return { ...floatingGroup, data: nextGroup } as SerializedFloatingGroupEntry
    })
    .filter(Boolean) as SerializedFloatingGroupEntry[]

  return prunedGroups.length > 0 ? prunedGroups : undefined
}

export function pruneLayoutPanels(
  layout: SerializedDockview,
  allowedPanelIds: Set<string>,
): SerializedDockview | null {
  const retainedEntries = Object.entries(layout.panels).filter(([id]) => allowedPanelIds.has(id))
  if (retainedEntries.length === 0) return null

  const nextRoot = pruneGrid(layout.grid.root, allowedPanelIds)
  const prunedFloatingGroups = pruneFloatingGroups(layout.floatingGroups, allowedPanelIds)

  if (!nextRoot && !prunedFloatingGroups) return null

  const retainedPanels: SerializedDockview["panels"] = Object.fromEntries(retainedEntries)
  const gridGroupIds = nextRoot ? collectGroupIds(nextRoot) : []
  const floatingGroupIds = prunedFloatingGroups?.map((fg) => (fg.data as GroupPanelViewState).id) ?? []
  const availableGroupIds = [...gridGroupIds, ...floatingGroupIds]
  const nextActiveGroup = layout.activeGroup && availableGroupIds.includes(layout.activeGroup)
    ? layout.activeGroup
    : availableGroupIds[0]

  return {
    ...omit(layout, "floatingGroups"),
    panels: retainedPanels,
    grid: {
      ...layout.grid,
      root: nextRoot ?? createEmptyGridRoot(layout.grid),
    },
    ...(prunedFloatingGroups ? { floatingGroups: prunedFloatingGroups } : {}),
    activeGroup: nextActiveGroup,
  }
}

export function stripPanelIdsFromLayout(
  layout: SerializedDockview,
  panelIdsToStrip: Set<string>,
): SerializedDockview | null {
  if (panelIdsToStrip.size === 0) {
    return layout
  }

  const allowedPanelIds = new Set(
    Object.keys(layout.panels).filter((panelId) => !panelIdsToStrip.has(panelId)),
  )

  return pruneLayoutPanels(layout, allowedPanelIds)
}
